import React, { createContext, useContext, useState } from 'react';

import { ENDPOINT_NOTIFICATIONS } from '@/common/constants';
import useNotificationsSubscription from '@/common/hooks/mercure/useNotificationsSubscription';
import useManageNotifications from '@/common/hooks/notifications/useManageNotifications';
import useNotifications from '@/common/hooks/notifications/useNotifications';
import {
  ChildrenDefaultProps,
  ChildrenPropTypes,
} from '@/common/proptypes/Base/Children.proptypes';
import { queryClient } from '@/common/providers';

const NotificationsContext = createContext(null);

export const NotificationsContextProvider = ({ children }) => {
  const [selectedType, setSelectedTypeContext] = useState('all');

  const queryKeyAll = [
    ENDPOINT_NOTIFICATIONS,
    {
      activeOnly: 0,
      isArchived: 0,
      isRead: 0,
      entityType: 'all',
    },
  ];

  const {
    data: allNotifications,
    fetchNextPage: fetchNextPageAll,
    hasNextPage: hasNextPageAll,
    isFetching,
  } = useNotifications(queryKeyAll, {
    activeOnly: 0,
    isArchived: 0,
    isRead: 0,
    entityType: 'all',
  });

  const queryKeyOrder = [
    ENDPOINT_NOTIFICATIONS,
    {
      activeOnly: 0,
      isArchived: 0,
      isRead: 0,
      entityType: 'order',
    },
  ];

  const {
    data: orderNotifications,
    fetchNextPage: fetchNextPageOrder,
    hasNextPage: hasNextPageOrder,
  } = useNotifications(queryKeyOrder, {
    activeOnly: 0,
    isArchived: 0,
    isRead: 0,
    entityType: 'order',
  });

  const queryKeyQuote = [
    ENDPOINT_NOTIFICATIONS,
    {
      activeOnly: 0,
      isArchived: 0,
      isRead: 0,
      entityType: 'quote',
    },
  ];

  const {
    data: quoteNotifications,
    fetchNextPage: fetchNextPageQuote,
    hasNextPage: hasNextPageQuote,
  } = useNotifications(queryKeyQuote, {
    activeOnly: 0,
    isArchived: 0,
    isRead: 0,
    entityType: 'quote',
  });

  const dataFormatter = (data) => {
    if (!data) return undefined;

    const formattedData = {
      meta: {},
      data: [],
    };

    data.pages.forEach((pageData, i) => {
      formattedData.data = [...formattedData.data, ...pageData.data];
      if (i === 0) {
        formattedData.meta = pageData.meta;
      }
    });

    return formattedData;
  };

  // Manage notifications (e.g., mark as read or archive)
  const { onUpdateNotifications } = useManageNotifications({
    disabled: false,
    isLoading: false,
  });

  // Function to handle notification updates (read or archive)
  const handleNotificationUpdate = (type, notificationIds) => {
    onUpdateNotifications({
      values: { type, notificationIds },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeyAll);
        queryClient.invalidateQueries(queryKeyOrder);
        queryClient.invalidateQueries(queryKeyQuote);
      },
    });
  };

  useNotificationsSubscription(queryKeyAll);
  useNotificationsSubscription(queryKeyOrder);
  useNotificationsSubscription(queryKeyQuote);

  return (
    <NotificationsContext.Provider
      value={{
        isFetching,
        selectedType,
        setSelectedTypeContext,
        onRead: handleNotificationUpdate,
        allNotifications: dataFormatter(allNotifications),
        orderNotifications: dataFormatter(orderNotifications),
        quoteNotifications: dataFormatter(quoteNotifications),
        fetchNextPageAll,
        fetchNextPageOrder,
        fetchNextPageQuote,
        hasNextPageAll,
        hasNextPageOrder,
        hasNextPageQuote,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

NotificationsContextProvider.defaultProps = {
  ...ChildrenDefaultProps,
};

NotificationsContextProvider.propTypes = {
  ...ChildrenPropTypes,
};

// Custom hook to use the notifications context
export const useNotificationsContext = () => useContext(NotificationsContext);
